import { ArrowLeft, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import AppHeader from '../components/AppHeader';
import ReportListRow from '../components/ReportListRow';
import useInformeList from '../hooks/useInformeList';
import AppPageLayout from '../layout/AppPageLayout';
import api from '../services/api';
import { getReportTypeOptions } from '../services/reportesService';

function formatReportDate(value) {
  if (!value) {
    return '-';
  }

  const hasTime = value.includes('T');
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  if (!hasTime) {
    return new Intl.DateTimeFormat('es-AR').format(date);
  }

  return new Intl.DateTimeFormat('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function ReportesPage() {
  const navigate = useNavigate();
  const { informes, loading, error, fetchAll, fetchByTipo } = useInformeList();
  const [reportType, setReportType] = useState('');
  const [search, setSearch] = useState('');
  const [searchInformes, setSearchInformes] = useState(null);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const reportTypeOptions = getReportTypeOptions();

  const reportesSource = searchInformes ?? informes;

  const filteredReportes = useMemo(() => {
    return reportesSource.filter((item) => {
      const query = search.trim().toLowerCase();
      const matchesSearch = query
        ? `${item.tipo} ${item.responsableId ?? ''} ${item.legajosSummary ?? ''} ${item.evaluadosSummary ?? ''
          } ${item.fecha}`
          .toLowerCase()
          .includes(query)
        : true;

      return matchesSearch;
    });
  }, [reportesSource, search]);

  const shouldShowList = Boolean(reportType || search.trim());
  const selectedReport = filteredReportes.find((item) => item.id === selectedReportId);

  const handleOpenReport = (report) => {
    setSelectedReportId(report.id);
    navigate(`/evaluaciones-trianuales/informe/${report.tipo}/${report.id}`);
  };

  const handleSearchByLegajo = async (legajo) => {
    setSelectedReportId(null);

    if (!legajo) {
      setSearchInformes(null);
      fetchAll().catch(() => null);
      return;
    }

    try {
      const entidadesResponse = await api.get(
        `/api/evento_evaluacion/entidades?legajo=${encodeURIComponent(legajo)}`
      );
      const entidades = entidadesResponse?.data ?? entidadesResponse ?? [];
      if (!entidades.length) {
        setSearchInformes([]);
        return;
      }

      const todasLasRespuestas = await Promise.all(
        entidades.map((entidad) =>
          api
            .get(`/api/informes/?evaluado=${entidad.id}&estado=4`)
            .then((r) => r?.data ?? r ?? [])
            .catch(() => [])
        )
      );

      const todosLosInformes = todasLasRespuestas.flat();
      if (!todosLosInformes.length) {
        setSearchInformes([]);
        return;
      }

      const informesNormalizados = todosLosInformes.map((informe) => {
        const entidad = entidades.find((e) => e.id === informe.id_evaluado);
        const legajoEntidad = entidad?.metadata
          ? (() => {
            try {
              const metadata =
                typeof entidad.metadata === 'string'
                  ? JSON.parse(entidad.metadata)
                  : entidad.metadata;
              return metadata.legajo || legajo;
            } catch {
              return legajo;
            }
          })()
          : legajo;

        return {
          id: informe.id,
          tipo: 'trianual',
          fecha: informe.fecha_programada || informe.alta,
          id_evaluacion: informe.id_evaluacion,
          evaluadosSummary: (informe.evaluado_nombre || '').trim() || '-',
          legajosSummary: legajoEntidad,
          _raw: informe,
        };
      });

      setSearchInformes(informesNormalizados);
    } catch (err) {
    }
  };

  return (
    <AppPageLayout>
      <AppHeader />

      <div className="mx-auto mt-10 w-full max-w-[760px] lg:mt-12">
        <h1 className="text-center font-login text-[30px] font-bold leading-tight text-[#30343b] sm:text-[36px]">
          Reportes
        </h1>

        <div className="mt-8 border-t border-[#eceff3] pt-6">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full text-[#111111] transition hover:bg-[#f5f7fa]"
            aria-label="Volver al dashboard"
          >
            <ArrowLeft className="h-5 w-5" strokeWidth={2.8} />
          </button>

          <div className="mt-6 space-y-5">
            <div>
              <label
                htmlFor="report-type"
                className="mb-2 block font-login text-[15px] font-bold text-[#30343b]"
              >
                Seleccione Tipo de Reporte
              </label>
              <select
                id="report-type"
                value={reportType}
                onChange={async (event) => {
                  const nextType = event.target.value;
                  setReportType(nextType);
                  setSelectedReportId(null);
                  setSearchInformes(null);

                  try {
                    if (nextType) {
                      await fetchByTipo(nextType);
                      return;
                    }

                    await fetchAll();
                  } catch (err) {
                    return err;
                  }
                }}
                className="h-11 w-full rounded-[10px] border border-[#1f2937] bg-white px-4 font-login text-[14px] text-[#374151] outline-none"
              >
                {reportTypeOptions.map((option) => (
                  <option
                    disabled={!option.value}
                    key={option.value || 'placeholder'}
                    value={option.value}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label
                htmlFor="report-search"
                className="mb-2 block font-login text-[15px] font-bold text-[#30343b]"
              >
                Seleccione el/los Legajo/s
              </label>
              <div className="flex overflow-hidden rounded-[8px] border border-[#1f2937] bg-white shadow-sm">
                <input
                  id="report-search"
                  type="text"
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setSelectedReportId(null);
                  }}
                  onKeyDown={async (event) => {
                    if (event.key === 'Enter') {
                      await handleSearchByLegajo(search.trim());
                    }
                  }}
                  placeholder="Legajo / Nombre"
                  className="h-11 flex-1 border-0 px-4 font-login text-[14px] text-[#374151] outline-none placeholder:text-[#9aa3ad]"
                />
                <button
                  type="button"
                  aria-label="Buscar"
                  onClick={() => handleSearchByLegajo(search.trim())}
                  className="flex h-11 w-12 items-center justify-center bg-[#67bb49] text-white"
                >
                  <Search className="h-4 w-4" strokeWidth={2.8} />
                </button>
              </div>
            </div>
          </div>

          {shouldShowList ? (
            <div className="mt-6 space-y-5">
              <div className="overflow-hidden rounded-[10px] border border-[#dce2e8] bg-white">
                <div className="grid grid-cols-[1fr_1fr_1.4fr_1fr] gap-3 border-b border-[#dce2e8] bg-[#f7f8fa] px-3 py-3">
                  <span className="font-login text-[10px] font-bold text-[#30343b] sm:text-[11px]">
                    FECHA
                  </span>
                  <span className="font-login text-[10px] font-bold text-[#30343b] sm:text-[11px]">
                    LEGAJO
                  </span>
                  <span className="font-login text-[10px] font-bold text-[#30343b] sm:text-[11px]">
                    EVALUADOS
                  </span>
                  <span className="font-login text-[10px] font-bold text-[#30343b] sm:text-[11px]">
                    EVALUACION
                  </span>
                </div>

                <div className="divide-y divide-[#dce2e8]">
                  {loading ? (
                    <p className="px-4 py-5 font-login text-[14px] text-[#5f6673]">
                      Cargando informes...
                    </p>
                  ) : null}

                  {!loading && error ? (
                    <p className="px-4 py-5 font-login text-[14px] text-[#c43c2f]">
                      No se pudieron cargar los informes.
                    </p>
                  ) : null}

                  {!loading && !error && !filteredReportes.length ? (
                    <p className="px-4 py-5 font-login text-[14px] text-[#5f6673]">
                      No hay informes para este tipo.
                    </p>
                  ) : null}

                  {filteredReportes.map((item) => (
                    <ReportListRow
                      key={item.id}
                      fecha={formatReportDate(item.fecha)}
                      legajo={item.legajosSummary ?? item.evaluado?.legajo ?? '-'}
                      evaluados={item.evaluadosSummary ?? '-'}
                      evaluacion={item.tipo}
                      isSelected={selectedReportId === item.id}
                      onClick={() => handleOpenReport(item)}
                    />
                  ))}
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  if (!selectedReport) {
                    return;
                  }

                  navigate(
                    `/evaluaciones-trianuales/informe/${selectedReport.tipo}/${selectedReport.id}`
                  );
                }}
                disabled={!selectedReportId}
                className="w-full rounded-[12px] bg-gradient-to-b from-[#1d9657] to-[#177d4a] px-4 py-4 font-login text-[15px] font-bold text-white shadow-[0_5px_10px_rgba(15,23,42,0.24)] transition hover:-translate-y-0.5 hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Confirmar
              </button>
            </div>
          ) : null}
        </div>

        <div className="pt-10 text-center font-login text-[10px] text-[#5f6673]">
          <p>Copyright 2022 JPH Lions Todos los derechos reservados</p>
        </div>
      </div>
    </AppPageLayout>
  );
}

export default ReportesPage;
